/**
 * Client game store — the hub between network, React HUD and the Pixi renderer.
 *
 *  - Network handlers call `receiveGameStart` / `receiveTick`.
 *  - The renderer reads `useGame.getState()` every frame (no React re-render cost) and forwards
 *    pointer input to the interaction actions (`hoverHex`, `clickHex`, `boxSelect` …).
 *  - React components subscribe with selectors: `useGame((s) => s.view?.me.manpower)`.
 *  - The replay viewer ("The Fog Lifts", replay/controller.ts) feeds recorded views through
 *    `receiveReplayFrame`; while `replay` is set the store is read-only (inspect, never order).
 *  - An observer (a capitulated nation of a free-for-all, `observe()`) receives the whole truth
 *    or another staff's view live (`receiveSpectate`, then ticks as usual): also read-only, but
 *    the clock stays the host's to set.
 */
import {
  applyViewDelta,
  type Command,
  type FactionId,
  type GameEvent,
  type GameMap,
  HexGrid,
  type HexId,
  type IntelContact,
  NO_HEX,
  type OrderKind,
  type PlayerView,
  type Posture,
  type SessionConfig,
  SPECTATOR,
  type Tick,
  type Unit,
  type UnitId,
  type ViewDelta,
} from '@friction/engine';
import { type GameSpeed, SPEED_TICK_MS } from '@friction/protocol';
import { createStore, useStore } from 'zustand';
import { send } from '../net/connection';
import { forTheRecord } from '../replay/timeline';

/** Active map tool. `select` = default (left-click selects, right-click issues the contextual order). */
export type MapTool = 'select' | 'move' | 'attack' | 'retreat';

export interface OrderDraft {
  /** Posture sent along with move/attack orders (null = keep current). */
  posture: Posture | null;
  /** H-hour as an absolute tick, or null to execute on receipt. */
  hHour: Tick | null;
  /** Preferred channel for orders outside telephone range. */
  channel: 'auto' | 'courier' | 'radio';
  opName: string | null;
}

export interface GameStoreState {
  // ---- static match data
  map: GameMap | null;
  grid: HexGrid | null;
  config: SessionConfig | null;
  faction: FactionId;

  // ---- live view
  view: PlayerView | null;
  /** Previous view, used by the renderer to interpolate movement between ticks. */
  prevView: PlayerView | null;
  /** performance.now() when `view` arrived. */
  viewReceivedAt: number;
  /** Expected ms between ticks at the current speed (0 when paused). */
  tickMs: number;
  speed: GameSpeed;
  paused: boolean;
  /** Rolling log of events for the teletype feed (newest last). */
  log: GameEvent[];
  /** Events that arrived with the latest tick (renderer uses them for one-shot effects). */
  freshEvents: GameEvent[];
  /**
   * The view is a replay frame (possibly omniscient, `faction` = SPECTATOR): any unit in the view
   * can be inspected, no command is ever sent, and the match chronicle ignores the changes.
   */
  replay: boolean;
  /**
   * Replay in a faction's perspective with "truth" on: where that faction's enemies really
   * stood (drawn faintly under its own intel). Null otherwise.
   */
  truth: Unit[] | null;
  /**
   * Observing the war (our nation has capitulated in a free-for-all): the perspective streamed —
   * SPECTATOR for the whole truth, or a faction's staff view. Null while we see our own view.
   */
  observing: FactionId | null;
  /** Our own staff's last view before observing began (the after-action report's record). */
  ownView: PlayerView | null;

  // ---- interaction
  selection: UnitId[];
  /** Enemy contact currently inspected (clicked). */
  inspectedContact: UnitId | null;
  hoverHex: HexId;
  tool: MapTool;
  draft: OrderDraft;

  // ---- lifecycle actions (called by net layer)
  receiveGameStart(p: {
    map: GameMap;
    config: SessionConfig;
    faction: FactionId;
    view: PlayerView;
    speed: GameSpeed;
    paused: boolean;
  }): void;
  receiveTick(delta: ViewDelta): void;
  receiveSpeed(speed: GameSpeed, paused: boolean): void;
  /** The host's answer to `observe`: a full view from `perspective` (resets the delta baseline). */
  receiveSpectate(perspective: FactionId, view: PlayerView): void;
  /**
   * Show a replay frame. `jump` (seek, new perspective) disables interpolation from the previous
   * frame and replaces the teletype log with `log`; otherwise `log` is appended to it.
   */
  receiveReplayFrame(f: {
    map: GameMap;
    config: SessionConfig;
    view: PlayerView;
    tickMs: number;
    jump: boolean;
    log: GameEvent[];
    truth: Unit[] | null;
  }): void;
  reset(): void;

  // ---- interaction actions (called by renderer + HUD)
  setHover(hex: HexId): void;
  select(ids: UnitId[], additive?: boolean): void;
  inspectContact(id: UnitId | null): void;
  setTool(tool: MapTool): void;
  setDraft(patch: Partial<OrderDraft>): void;
  /** Left click on a hex (after the renderer resolved it). */
  clickHex(hex: HexId, opts: { shift: boolean; unitId?: UnitId; contactId?: UnitId }): void;
  /** Right click on a hex: issue the contextual order for the selection. */
  commandHex(hex: HexId, opts: { shift: boolean }): void;
  /** Rubber-band selection result from the renderer. */
  boxSelect(ids: UnitId[], additive: boolean): void;
  /** Issue an order of the given kind to the current selection. */
  issueOrder(kind: OrderKind, target: HexId, posture?: Posture): void;
  sendCommand(command: Command): void;
  setSpeed(speed: GameSpeed): void;
  setPaused(paused: boolean): void;
  /** Ask the host to let us observe from `perspective` (SPECTATOR = the whole truth). */
  observe(perspective: FactionId): void;
}

const LOG_LIMIT = 300;

const initialDraft: OrderDraft = { posture: null, hHour: null, channel: 'auto', opName: null };

let cmdSeq = 1;

/** Inspect any unit, order none: a replay frame or an observer's view. */
export const isReadOnly = (s: Pick<GameStoreState, 'replay' | 'observing'>): boolean =>
  s.replay || s.observing !== null;

/**
 * The teletype of the whole truth prints the war, not every staff's signals traffic (as the
 * replay's omniscient teletype does).
 */
const printable = (events: GameEvent[], observing: FactionId | null): GameEvent[] =>
  observing === SPECTATOR ? events.filter(forTheRecord) : events;

export const gameStore = createStore<GameStoreState>()((set, get) => ({
  map: null,
  grid: null,
  config: null,
  faction: 0,
  view: null,
  prevView: null,
  viewReceivedAt: 0,
  tickMs: 0,
  speed: 3,
  paused: false,
  log: [],
  freshEvents: [],
  replay: false,
  truth: null,
  observing: null,
  ownView: null,
  selection: [],
  inspectedContact: null,
  hoverHex: NO_HEX,
  tool: 'select',
  draft: initialDraft,

  receiveGameStart({ map, config, faction, view, speed, paused }) {
    set({
      map,
      grid: new HexGrid(map.width, map.height),
      config,
      faction,
      view,
      prevView: view,
      viewReceivedAt: performance.now(),
      speed,
      paused,
      tickMs: paused ? 0 : SPEED_TICK_MS[speed],
      log: view.events.slice(-LOG_LIMIT),
      freshEvents: view.events,
      replay: false,
      truth: null,
      observing: null,
      ownView: null,
      selection: [],
      inspectedContact: null,
      tool: 'select',
      draft: initialDraft,
    });
  },

  receiveTick(delta) {
    const { view, log, selection, prevView, viewReceivedAt, observing } = get();
    if (!view) return;
    const next = applyViewDelta(view, delta);
    // A same-tick delta (orders applied between ticks) must not restart movement interpolation.
    const sameTick = delta.tick === view.tick;
    // Drop selected units that no longer exist.
    const alive = new Set(next.units.map((u) => u.id));
    const nextSelection = selection.filter((id) => alive.has(id));
    const printed = printable(delta.events, observing);
    set({
      prevView: sameTick ? prevView : view,
      view: next,
      viewReceivedAt: sameTick ? viewReceivedAt : performance.now(),
      freshEvents: delta.events,
      log: printed.length ? [...log, ...printed].slice(-LOG_LIMIT) : log,
      selection: nextSelection.length === selection.length ? selection : nextSelection,
    });
  },

  receiveSpeed(speed, paused) {
    set({ speed, paused, tickMs: paused ? 0 : SPEED_TICK_MS[speed] });
  },

  receiveSpectate(perspective, view) {
    const s = get();
    set({
      view,
      // A new perspective is a jump: no interpolation from what another staff believed.
      prevView: view,
      viewReceivedAt: performance.now(),
      observing: perspective,
      ownView: s.observing === null ? s.view : s.ownView,
      log: printable(view.events, perspective).slice(-LOG_LIMIT),
      freshEvents: view.events,
      selection: [],
      inspectedContact: null,
      tool: 'select',
    });
  },

  receiveReplayFrame({ map, config, view, tickMs, jump, log, truth }) {
    const s = get();
    const fresh = s.map !== map || !s.view;
    const alive = new Set(view.units.map((u) => u.id));
    const selection = s.selection.filter((id) => alive.has(id));
    set({
      map,
      grid: fresh || !s.grid ? new HexGrid(map.width, map.height) : s.grid,
      config,
      faction: view.faction,
      view,
      prevView: jump || fresh ? view : s.view,
      viewReceivedAt: performance.now(),
      tickMs,
      paused: tickMs === 0,
      log: (jump || fresh ? log : log.length ? [...s.log, ...log] : s.log).slice(-LOG_LIMIT),
      freshEvents: view.events,
      replay: true,
      truth,
      selection: selection.length === s.selection.length ? s.selection : selection,
      inspectedContact:
        s.inspectedContact !== null && view.contacts.some((c) => c.unitId === s.inspectedContact)
          ? s.inspectedContact
          : null,
      tool: 'select',
    });
  },

  reset() {
    set({
      map: null,
      grid: null,
      config: null,
      view: null,
      prevView: null,
      log: [],
      freshEvents: [],
      replay: false,
      truth: null,
      observing: null,
      ownView: null,
      selection: [],
      inspectedContact: null,
      hoverHex: NO_HEX,
      tool: 'select',
      draft: initialDraft,
    });
  },

  setHover(hex) {
    if (get().hoverHex !== hex) set({ hoverHex: hex });
  },

  select(ids, additive = false) {
    const s = get();
    const { view, faction } = s;
    // A replay or an observer inspects any unit in the frame (all of them in the whole truth).
    const any = isReadOnly(s);
    const own = new Set(view?.units.filter((u) => any || u.faction === faction).map((u) => u.id));
    const valid = ids.filter((id) => own.has(id));
    set((s) => ({
      selection: additive ? Array.from(new Set([...s.selection, ...valid])) : valid,
      inspectedContact: null,
    }));
  },

  inspectContact(id) {
    set({ inspectedContact: id });
  },

  setTool(tool) {
    set({ tool });
  },

  setDraft(patch) {
    set((s) => ({ draft: { ...s.draft, ...patch } }));
  },

  clickHex(hex, { shift, unitId, contactId }) {
    const s = get();
    // An active order tool turns a left click into that order.
    if (s.tool !== 'select' && s.selection.length > 0) {
      s.issueOrder(s.tool, hex);
      if (!shift) set({ tool: 'select' });
      return;
    }
    if (unitId !== undefined) {
      s.select([unitId], shift);
      return;
    }
    if (contactId !== undefined) {
      set({ inspectedContact: contactId, selection: shift ? s.selection : [] });
      return;
    }
    // Clicking a hex with own units selects them all (stack).
    const own = unitsAt(s.view, hex, isReadOnly(s) ? undefined : s.faction);
    if (own.length)
      s.select(
        own.map((u) => u.id),
        shift,
      );
    else if (!shift) set({ selection: [], inspectedContact: null });
  },

  commandHex(hex, { shift }) {
    const s = get();
    if (!s.selection.length || hex === NO_HEX || isReadOnly(s)) return;
    const hostile = contactsAt(s.view, hex).length > 0;
    s.issueOrder(hostile ? 'attack' : s.tool === 'select' ? 'move' : s.tool, hex);
    if (!shift) set({ tool: 'select' });
  },

  boxSelect(ids, additive) {
    get().select(ids, additive);
  },

  issueOrder(kind, target, posture) {
    const s = get();
    if (!s.selection.length || isReadOnly(s)) return;
    const d = s.draft;
    s.sendCommand({
      type: 'order',
      unitIds: s.selection,
      kind,
      target,
      posture: posture ?? d.posture ?? undefined,
      executeAt: d.hHour ?? -1,
      channel: d.channel,
      opName: d.opName ?? undefined,
    });
  },

  sendCommand(command) {
    if (!isReadOnly(get())) send({ t: 'cmd', seq: cmdSeq++, command });
  },

  // The clock is the host's also while observing (the server checks who may set it).
  setSpeed(speed) {
    if (!get().replay) send({ t: 'setSpeed', speed });
  },

  setPaused(paused) {
    if (!get().replay) send({ t: 'pause', paused });
  },

  observe(perspective) {
    if (!get().replay) send({ t: 'spectate', perspective });
  },
}));

/** React hook with selector: `const tick = useGame((s) => s.view?.tick)`. */
export function useGame<T>(selector: (s: GameStoreState) => T): T {
  return useStore(gameStore, selector);
}

// ---- helpers shared by HUD and renderer -------------------------------------------------------

export function unitsAt(view: PlayerView | null, hex: HexId, faction?: FactionId): Unit[] {
  if (!view) return [];
  return view.units.filter((u) => u.hex === hex && (faction === undefined || u.faction === faction));
}

export function contactsAt(view: PlayerView | null, hex: HexId): IntelContact[] {
  if (!view) return [];
  return view.contacts.filter((c) => c.hex === hex);
}
